import React, { useMemo, useState } from "react";
import Text from "../../Tags/Text";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { Button } from "../../ui/button";
import Image from "../../Tags/Image";
import { checkoutPlan } from "../../../apis/workspace/billing";
import { useMutation } from "react-query";
import { useCaptureEvent } from "../../../hooks/useCaptureEvent";

const formatCurrency = (amount, currencyCode = "USD") => {
  if (!Number.isFinite(amount)) {
    return "-";
  }

  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currencyCode,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch (error) {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount);
  }
};

export default function Plan({ interval, plan, available_quantity, activeWorkspace, activePlan = null }) {
  const [quantity, setQuantity] = useState(
    plan?.pricing?.[interval]?.subscription_quantity || available_quantity[0]
  );
  const captureEvent = useCaptureEvent();

  const checkoutPlanFn = useMutation("checkout_plan", checkoutPlan, {
    onSuccess(data, variables, context) {
      console.log("checkout_plan", data);
      if (data?.url) {
        window.location.href = data.url;
      }
      captureEvent("BP_CheckoutSuccess", {
        plan_name: plan?.name,
        interval,
        quantity: variables?.data?.quantity,
      });
    },
    onError(error) {
      console.log(error);
      captureEvent("BP_CheckoutFailure", {
        plan_name: plan?.name,
        interval,
        quantity,
        error: error?.message || "unknown_error",
      });
    },
  });
  const handleCheckoutPlan = () => {
    if (checkoutPlanFn.isLoading) {
      return;
    }
    captureEvent("BP_CheckoutClick", {
      plan_name: plan?.name,
      interval,
      quantity,
    });
    checkoutPlanFn.mutate({
      data: {
        quantity: quantity,
        stripe_price_id: plan?.pricing[interval]?.stripe_id,
        success_url: `${process.env.REACT_APP_BASE_URL}/${process.env.REACT_APP_APP_HOME}/${activeWorkspace?.slug}/settings/billing/plans`,
        cancel_url: `${process.env.REACT_APP_BASE_URL}/${process.env.REACT_APP_APP_HOME}/${activeWorkspace?.slug}/settings/billing/plans`,
      },
      options: {
        headers: { "X-Workspace-Id": activeWorkspace?.id },
      },
    });
  };

  const pricingDetails = useMemo(() => {
    const unitAmountCents = Number(plan?.pricing?.[interval]?.unit_amount_cents ?? 0);
    const currencyCode = plan?.pricing?.[interval]?.currency?.toUpperCase?.() ?? "USD";
    const billedAmount = (unitAmountCents / 100) * quantity;
    const displayedAmount = interval !== "monthly" ? billedAmount / 12 : billedAmount;
    // const displayedAmount = billedAmount / 12;
    return {
      currencyCode,
      displayedAmount,
      formattedAmount: formatCurrency(displayedAmount, currencyCode),
    };
  }, [interval, plan?.pricing, quantity]);

  const renderUpgradeButton = (is_subscribed, current_quantity) => {
    if (activePlan && activePlan?.quantity === current_quantity && is_subscribed) {
      return (
        <Button
          variant="secondary"
          disabled={true}
          className="text-gray-700 w-full my-6 border border-border"
        >
          <svg width="15" height="11" viewBox="0 0 15 11" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M14.1668 1L5.00016 10.1667L0.833496 6"
              stroke="#344054"
              stroke-width="1.66667"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          <span className="ml-2">Current plan</span>
        </Button>
      );
    } else if (
      activePlan &&
      activePlan?.quantity &&
      (activePlan?.quantity > current_quantity || !is_subscribed)
    ) {
      return (
        <Button
          variant="purple_outline"
          className=" w-full my-6"
          disabled={checkoutPlanFn.isLoading}
          onClick={handleCheckoutPlan}
        >
          <svg width="16" height="20" viewBox="0 0 16 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M8.83354 1.66675L1.41142 10.5733C1.12075 10.9221 0.975413 11.0965 0.973192 11.2438C0.971261 11.3718 1.02832 11.4937 1.12792 11.5742C1.2425 11.6667 1.46952 11.6667 1.92357 11.6667H8.00021L7.16688 18.3334L14.589 9.42687C14.8797 9.07806 15.025 8.90366 15.0272 8.75636C15.0292 8.62832 14.9721 8.50649 14.8725 8.426C14.7579 8.33342 14.5309 8.33342 14.0768 8.33342H8.00021L8.83354 1.66675Z"
              stroke="currentColor"
              stroke-width="1.66667"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          <span className="ml-2">Switch plan</span>
          {checkoutPlanFn.isLoading && <div className="loader_circle ml-2 inline-block"></div>}
        </Button>
      );
    } else {
      return (
        <Button className=" w-full my-6" disabled={checkoutPlanFn.isLoading} onClick={handleCheckoutPlan}>
          <svg width="16" height="20" viewBox="0 0 16 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M8.83354 1.66675L1.41142 10.5733C1.12075 10.9221 0.975413 11.0965 0.973192 11.2438C0.971261 11.3718 1.02832 11.4937 1.12792 11.5742C1.2425 11.6667 1.46952 11.6667 1.92357 11.6667H8.00021L7.16688 18.3334L14.589 9.42687C14.8797 9.07806 15.025 8.90366 15.0272 8.75636C15.0292 8.62832 14.9721 8.50649 14.8725 8.426C14.7579 8.33342 14.5309 8.33342 14.0768 8.33342H8.00021L8.83354 1.66675Z"
              stroke="currentColor"
              stroke-width="1.66667"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          <span className="ml-2">{activePlan ? "Upgrade plan" : "Get started"}</span>
          {checkoutPlanFn.isLoading && <div className="loader_circle ml-2 inline-block"></div>}
        </Button>
      );
    }
  };

  return (
    <div
      className={`leading-5 relative border bg-background py-4 px-6 rounded-xl ${plan?.name === "Pro" ? "border-brand-500" : "border-border"}`}
    >
      {plan?.name === "Pro" && (
        <Image
          src="/images/icons/billing/most-popular.svg"
          className="inline-block no-invert absolute right-0 -top-7"
        />
      )}
      <Text size="lg">{plan?.name}</Text>
      <Text>{plan?.description}</Text>
      <div className="flex justify-center my-7 items-center">
        <Text size="h4">{pricingDetails.formattedAmount}</Text>
        <div className="ml-3">
          <p>
            <span className="uppercase">{pricingDetails.currencyCode}</span> / Month
          </p>
          {interval !== "monthly" ? (
            <p className="text-xs text-gray-500">Billed annually</p>
          ) : (
            <p className="text-xs text-gray-500">Billed monthly</p>
          )}
        </div>
      </div>
      <Select
        value={quantity}
        onValueChange={(value) => {
          setQuantity(value);
          captureEvent("BP_QuantityChange", {
            plan_name: plan?.name,
            interval,
            quantity: value,
          });
        }}
      >
        <SelectTrigger className="justify-between gap-2">
          <svg width="20" height="18" viewBox="0 0 20 18" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M10.8332 3.16667C10.8332 4.08714 8.78114 4.83333 6.24984 4.83333C3.71853 4.83333 1.6665 4.08714 1.6665 3.16667M10.8332 3.16667C10.8332 2.24619 8.78114 1.5 6.24984 1.5C3.71853 1.5 1.6665 2.24619 1.6665 3.16667M10.8332 3.16667V4.41667M1.6665 3.16667V13.1667C1.6665 14.0871 3.71853 14.8333 6.24984 14.8333M6.24984 8.16667C6.10938 8.16667 5.9704 8.16437 5.83317 8.15987C3.49713 8.08332 1.6665 7.3694 1.6665 6.5M6.24984 11.5C3.71853 11.5 1.6665 10.7538 1.6665 9.83333M18.3332 8.58333C18.3332 9.50381 16.2811 10.25 13.7498 10.25C11.2185 10.25 9.1665 9.50381 9.1665 8.58333M18.3332 8.58333C18.3332 7.66286 16.2811 6.91667 13.7498 6.91667C11.2185 6.91667 9.1665 7.66286 9.1665 8.58333M18.3332 8.58333V14.8333C18.3332 15.7538 16.2811 16.5 13.7498 16.5C11.2185 16.5 9.1665 15.7538 9.1665 14.8333V8.58333M18.3332 11.7083C18.3332 12.6288 16.2811 13.375 13.7498 13.375C11.2185 13.375 9.1665 12.6288 9.1665 11.7083"
              stroke="#414651"
              stroke-width="1.66667"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>

          <SelectValue className="flex-1 text-center" placeholder="Credits" />
        </SelectTrigger>
        <SelectContent>
          {available_quantity &&
            available_quantity.length > 0 &&
            available_quantity.map((el) => (
              <SelectItem key={el} value={el} className="justify-center text-center">
                {el} credits per {interval === "monthly" ? "month" : "year"}
              </SelectItem>
            ))}
        </SelectContent>
      </Select>

      {renderUpgradeButton(plan?.pricing?.[interval]?.is_subscribed, quantity)}

      <div className="space-y-3 pb-4 border-b">
        <div className="flex items-center">
          <Image src="/images/icons/billing/stack.svg" width="16" className="inline-block no-invert" />
          <span className="ml-2">
            {quantity} emails or {quantity / 5} phone numbers
          </span>
        </div>
        <div className="flex items-center">
          <Image src="/images/icons/billing/user.svg" width="16" className="inline-block no-invert" />
          <span className="ml-2">Unlimited user seats</span>
        </div>
        <div className="flex items-center">
          <Image src="/images/icons/billing/bag-uparrow.svg" width="16" className="inline-block no-invert" />
          <span className="ml-2">All credits granted upfront</span>
        </div>
      </div>
      <div className="space-y-3 pb-4 border-b mt-4">
        <div className="flex items-center justify-between">
          <div className="inline-flex items-center">
            <Image src="/images/icons/billing/email.svg" width="16" className="inline-block no-invert" />
            <span className="ml-2">Reveal contact’s emails</span>
          </div>
          <div className="inline-flex items-center">
            <span className="mr-2">1</span>
            <Image src="/images/icons/billing/credit.svg" width="16" className="inline-block no-invert" />
          </div>
        </div>
        <div className="flex items-center justify-between">
          <div className="inline-flex items-center">
            <Image src="/images/icons/billing/phone.svg" width="16" className="inline-block no-invert" />
            <span className="ml-2">Reveal contact’s phones</span>
          </div>
          <div className="inline-flex items-center">
            <span className="mr-2">5</span>
            <Image src="/images/icons/billing/credit.svg" width="16" className="inline-block no-invert" />
          </div>
        </div>
        <div className="flex items-center justify-between">
          <div className="inline-flex items-center">
            <Image src="/images/icons/billing/company.svg" width="16" className="inline-block no-invert" />
            <span className="ml-2">Reveal companies info</span>
          </div>
          <div className="inline-flex items-center">
            <span className="mr-2">3</span>
            <Image src="/images/icons/billing/credit.svg" width="16" className="inline-block no-invert" />
          </div>
        </div>
      </div>
      <div className="space-y-3 mt-4">
        {plan &&
          plan.features &&
          plan.features.length > 0 &&
          plan.features.map((feature) => (
            <div className="flex items-center">
              <Image
                src="/images/icons/untitledui/check-purple-outline.svg"
                width="16"
                className="inline-block no-invert"
              />
              <span className="ml-2">{feature}</span>
            </div>
          ))}
      </div>
    </div>
  );
}
